/**
 * usePushNotifications — Web Push subscription manager.
 * Auto-registers SW, requests permission, subscribes to VAPID push on first call.
 */
import { useEffect, useRef, useState } from "react";
import { getVapidKey, knyaPushSubscribe, knyaPushUnsubscribe } from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const STORAGE_KEY = "knya.push.subscribed";

export function usePushNotifications() {
  const [status, setStatus] = useState("idle"); // idle | requesting | subscribed | denied | unsupported
  const attempted = useRef(false);

  const subscribe = async () => {
    if (attempted.current) return;
    attempted.current = true;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported");
      return;
    }

    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      // Check existing subscription first
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        setStatus("subscribed");
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
        return;
      }

      // Ask permission
      setStatus("requesting");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus("denied");
        return;
      }

      // Get VAPID key
      const { public_key } = await getVapidKey();
      const applicationServerKey = urlBase64ToUint8Array(public_key);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Send to backend
      const subJson = sub.toJSON();
      await knyaPushSubscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        expirationTime: subJson.expirationTime,
      });

      setStatus("subscribed");
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    } catch (err) {
      console.warn("Push subscription failed:", err);
      setStatus("idle");
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJson = sub.toJSON();
      await knyaPushUnsubscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
        await sub.unsubscribe();
      }
      setStatus("idle");
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    } catch (err) {
      console.warn("Push unsubscribe failed:", err);
    }
  };

  return { status, subscribe, unsubscribe };
}
